import debounce from 'lodash.debounce'
import { useEffect, useMemo, useRef } from 'react'

import { useRecoilCallback, useRecoilValue } from 'recoil'

import type {
  FetchItemByIdFunction,
  FetchItemsByTermFunction,
  FetchItemsFunction,
  OptionEntryValue,
} from './model'

import { asyncSelectState } from './state'

import {
  onValueChangeCallback,
  resetSearchCallback,
  searchItemsCallback,
} from './callbacks'

export const memo = <R, T extends (arg: any) => R>(fn: T): T => {
  const memoMap = new Map()

  const memoized = (fArg: any): R =>
    memoMap.has(fArg)
      ? memoMap.get(fArg)
      : memoMap.set(fArg, fn(fArg)).get(fArg)

  memoized.$$clear = () => {
    memoMap.clear()
  }

  return memoized as unknown as T
}

const useAsyncSelect = ({
  key,
  value,
  maxItems,
  fetchItems,
  fetchItemsByTerm,
  fetchItemById,
  labels,
}: {
  key: string
  value: OptionEntryValue | OptionEntryValue[] | undefined
  maxItems: number
  fetchItems: FetchItemsFunction
  fetchItemsByTerm: FetchItemsByTermFunction
  fetchItemById: FetchItemByIdFunction
  labels: {
    loading: string
    moreResults: string
    noResults: string
  }
}): any => {
  const {
    status,
    loading,
    searching,
    initialOptions,
    initialOptionsCount,
    searchOptions,
    searchOptionsCount,
  } = useRecoilValue(asyncSelectState(key))

  const memoFetchItems = memo(fetchItems)
  const memoFetchItemsByTerm = memo(fetchItemsByTerm)
  const memoFetchItemById = memo(fetchItemById)

  const onValueChange = useRecoilCallback(onValueChangeCallback, [])
  const searchItems = useRecoilCallback(searchItemsCallback, [])
  const resetSearch = useRecoilCallback(resetSearchCallback, [])

  const updateOptions = (
    value: OptionEntryValue | OptionEntryValue[],
  ): void => {
    void onValueChange(key, value, memoFetchItems, memoFetchItemById)
  }

  useEffect(() => {
    void onValueChange(key, value, memoFetchItems, memoFetchItemById)
  }, [value])

  const options = useMemo(() => {
    return searching
      ? [
          ...searchOptions.filter(
            ({ value }) =>
              initialOptions.find(({ value: v }) => v === value) === undefined,
          ),
          ...initialOptions,
        ]
      : initialOptions
  }, [searching, initialOptions, searchOptions])

  //

  const hint = useMemo(() => {
    return loading
      ? labels.loading
      : searching && searchOptionsCount > maxItems
        ? labels.moreResults
        : !searching && initialOptionsCount > maxItems
          ? labels.moreResults
          : undefined
  }, [loading, searching, initialOptionsCount, searchOptionsCount])

  const hintNoResults = useMemo(() => {
    return !loading && searching && searchOptionsCount === 0
      ? labels.noResults
      : undefined
  }, [loading, searching, searchOptionsCount])

  const onSearch = (searchTerm: string | undefined): string | undefined => {
    if (searchTerm !== undefined && searchTerm.length > 0) {
      void searchItems(key, searchTerm, memoFetchItemsByTerm)
    } else {
      void resetSearch(key)
    }

    return searchTerm
  }

  const { current: debouncedOnSearch } = useRef(debounce(onSearch, 100))

  const handleSearch = (s: string): string => {
    debouncedOnSearch.cancel()
    debouncedOnSearch(s)
    return s
  }

  return {
    status,
    loading,
    searching,
    options,
    handleSearch,
    hint,
    hintNoResults,
    updateOptions,
  }
}

export default useAsyncSelect
